// Shared form validation used by both the browser (src/) and the Vercel
// functions (api/). Kept in CommonJS so Node can require() it directly and
// webpack can still import it.

// Vercel functions reject request bodies over 4.5 MB. The CV travels as
// base64 inside JSON (~33% larger), so 3 MB is the safe ceiling.
const MAX_CV_BYTES = 3 * 1024 * 1024;

const POSITIONS = [
  "Project Manager",
  "Site Engineer",
  "Quantity Surveyor",
  "Safety Officer",
  "Civil Engineer",
  "Accountant",
  "Maintenance Technician",
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_CHARS_RE = /^[0-9+\-()\s]+$/;

const str = (v) => (typeof v === "string" ? v.trim() : "");

function checkName(value, errors, field = "name") {
  if (!value) errors[field] = "Please enter your name.";
  else if (value.length < 2) errors[field] = "Name is too short.";
  else if (value.length > 100) errors[field] = "Name is too long.";
}

function checkEmail(value, errors) {
  if (!value) errors.email = "Please enter your email address.";
  else if (value.length > 254 || !EMAIL_RE.test(value))
    errors.email = "Please enter a valid email address.";
}

function checkPhone(value, errors) {
  const digits = value.replace(/\D/g, "");
  if (!value) errors.phone = "Please enter your phone number.";
  else if (!PHONE_CHARS_RE.test(value) || digits.length < 7 || digits.length > 15)
    errors.phone = "Please enter a valid phone number.";
}

function validateContact(input) {
  const data = {
    name: str(input && input.name),
    company: str(input && input.company),
    phone: str(input && input.phone),
    email: str(input && input.email),
    message: str(input && input.message),
  };
  const errors = {};
  checkName(data.name, errors);
  if (data.company.length > 150) errors.company = "Company name is too long.";
  checkPhone(data.phone, errors);
  checkEmail(data.email, errors);
  if (!data.message) errors.message = "Please tell us about your enquiry.";
  else if (data.message.length < 10) errors.message = "Please add a little more detail.";
  else if (data.message.length > 5000) errors.message = "Enquiry is too long (5000 characters max).";
  return { data, errors, valid: Object.keys(errors).length === 0 };
}

// Identify a CV by its leading bytes, not its filename.
// Returns "pdf" | "doc" | "docx" | null.
function detectFileType(bytes) {
  if (!bytes || bytes.length < 8) return null;
  const startsWith = (sig) => sig.every((b, i) => bytes[i] === b);

  if (startsWith([0x25, 0x50, 0x44, 0x46, 0x2d])) return "pdf"; // %PDF-
  if (startsWith([0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1])) return "doc"; // OLE2
  if (startsWith([0x50, 0x4b, 0x03, 0x04])) {
    // A .docx is a zip that contains a word/ folder; any other zip is rejected.
    const marker = [0x77, 0x6f, 0x72, 0x64, 0x2f]; // "word/"
    outer: for (let i = 0; i <= bytes.length - marker.length; i++) {
      for (let j = 0; j < marker.length; j++) {
        if (bytes[i + j] !== marker[j]) continue outer;
      }
      return "docx";
    }
  }
  return null;
}

function extensionOf(filename) {
  const match = /\.([a-z0-9]+)$/i.exec(filename || "");
  return match ? match[1].toLowerCase() : "";
}

// Validates the CV metadata and bytes. `bytes` may be omitted on the client
// before the file has been read.
function validateCv(cv, bytes) {
  if (!cv || !cv.name) return "Please attach your CV.";
  const ext = extensionOf(cv.name);
  if (!["pdf", "doc", "docx"].includes(ext)) return "CV must be a PDF, DOC or DOCX file.";
  const size = bytes ? bytes.length : cv.size;
  if (!size) return "The selected file is empty.";
  if (size > MAX_CV_BYTES) return "CV must be 3 MB or smaller.";
  if (bytes) {
    const detected = detectFileType(bytes);
    if (!detected) return "This file doesn't look like a real PDF or Word document.";
    if (detected !== ext) return `This file's contents don't match its .${ext} extension.`;
  }
  return null;
}

function validateCareer(input, cvBytes) {
  const data = {
    name: str(input && input.name),
    email: str(input && input.email),
    phone: str(input && input.phone),
    position: str(input && input.position),
    age: str(input && input.age != null ? String(input.age) : ""),
  };
  const errors = {};
  checkName(data.name, errors);
  checkEmail(data.email, errors);
  checkPhone(data.phone, errors);
  if (!data.position) errors.position = "Please choose a position.";
  else if (!POSITIONS.includes(data.position)) errors.position = "Please choose a position from the list.";
  const age = Number(data.age);
  if (!data.age) errors.age = "Please enter your age.";
  else if (!Number.isInteger(age) || age < 18 || age > 100)
    errors.age = "Age must be a whole number between 18 and 100.";
  const cvError = validateCv(input && input.cv, cvBytes);
  if (cvError) errors.cv = cvError;
  return { data, errors, valid: Object.keys(errors).length === 0 };
}

module.exports = {
  MAX_CV_BYTES,
  POSITIONS,
  validateContact,
  validateCareer,
  validateCv,
  detectFileType,
  extensionOf,
};
